import "#/debug"
import { css, cx } from "@emotion/css"
import { Button, Tab, Tabs } from "@heroui/react"
import { Plural, Trans, useLingui } from "@lingui/react/macro"
import { FolderOpen, PanelLeftClose, PanelLeftOpen } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { useDrop } from "react-aria"
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels"

import { Boundary } from "#/components/ErrorOverlay"
import { Main, RootGrid, TopBar } from "#/components/Panels"
import { ThemeSwitch } from "#/toolbar/DarkMode"
import { openFileWithHandle } from "#/toolbar/file"
import { LocaleSwitch } from "#/toolbar/Locale"
import { getHost, id } from "#/utils"

import { AboutSwitch } from "./components/AboutModal"
import { DROP_HAR_TIPS } from "./components/DropHarTips"
import { EntityList } from "./components/EntryList"
import { ViewStateDropdown } from "./components/ViewStateDropdown"
import type { Har } from "./har"
import _har from "./sample.json" with { type: "json" }
import { useSampleData } from "./sampleData"
import { BodyTab } from "./tabs/Body"
import { CookiesTab } from "./tabs/Cookies"
import { RequestTab } from "./tabs/Request"
import { ResponseTab } from "./tabs/Response"
import { TimelineTab } from "./tabs/Timeline"
import { HostTypeButton } from "./toolbar/HostTypeModal"
import { MethodFilter } from "./toolbar/MethodFilter"
import { MimeTypeButton } from "./toolbar/MimeTypeModal"
import { StatusCodeFilter } from "./toolbar/StatusCodeFilter"
import { DevPlayground } from "./components/DevPlayground"

enum ViewTab {
  Request = "request",
  Response = "response",
  Content = "content",
  Cookies = "cookies",
  Timeline = "timeline",
}

function App() {
  const { t } = useLingui()
  const [tab, setTab] = useState(ViewTab.Request)
  const rightTab = useRef<HTMLDivElement>(undefined!)

  const [showSidebar, setShowSidebar] = useState(true)

  const [selected, setSelected] = useState<string>()
  const [filename, setFilename] = useState<string>()
  const [har, setHar] = useState<Har | undefined>()
  useSampleData(setHar)

  useEffect(() => {
    if ("launchQueue" in window) {
      window.launchQueue!.setConsumer(async launchParams => {
        for (const file of launchParams.files) {
          if (file.kind === "file") {
            const handle = file as FileSystemFileHandle
            await open(await handle.getFile())
            break
          }
        }
      })
    }
  }, [])

  const [compact, setCompact] = useState(true)
  const [filterStatus, setFilterStatus] = useState<string>()
  const [methodFilter, setMethodFilter] = useState<string[]>([])
  const [mimeFilter, setMimeFilter] = useState(() => new Set<string>())
  const [excludeHost, setExcludeHost] = useState(() => new Set<string>())
  const [excludeMimeType, setExcludeMimeType] = useState(() => new Set<string>())
  const [hostFilter, setHostFilter] = useState(() => new Set<string>())

  const exportState = () => ({
    compact,
    filterStatus,
    methodFilter,
    mimeFilter: [...mimeFilter],
    hostFilter: [...hostFilter],
    excludeHost: [...excludeHost],
    excludeMimeType: [...excludeMimeType],
  })

  if (har) {
    Object.assign(globalThis, {
      har: har.log,
      list: har.log.entries,
    })
  }

  const activeEntry = har?.log.entries.find(entry => id(entry) === selected)

  const entries = useMemo(() => {
    let list = har?.log.entries ?? []

    if (filterStatus) {
      list = list.filter(e => e.response.status.toString()[0] === filterStatus)
    }
    if (mimeFilter.size) {
      list = list.filter(
        e =>
          e.response.content.mimeType &&
          mimeFilter.has(e.response.content.mimeType.split(";")[0])
      )
    }
    if (excludeMimeType.size) {
      list = list.filter(
        e =>
          !e.response.content.mimeType ||
          !excludeMimeType.has(e.response.content.mimeType.split(";")[0])
      )
    }

    if (hostFilter.size) {
      list = list.filter(e => hostFilter.has(getHost(e.request.url)))
    }
    if (excludeHost.size) {
      list = list.filter(e => !excludeHost.has(getHost(e.request.url)))
    }
    if (methodFilter.length) {
      const set = new Set(methodFilter)
      list = list.filter(e => set.has(e.request.method))
    }

    return list
  }, [
    har,
    filterStatus,
    mimeFilter,
    hostFilter,
    methodFilter,
    excludeMimeType,
    excludeHost,
  ])

  async function open(file: File) {
    setFilename(file.name)
    try {
      const json = JSON.parse(await file.text()) as Har
      if (!Array.isArray(json?.log?.entries)) {
        alert(t`This file is not a valid HAR file.`)
        return
      }
      setHar(json)
      setShowSidebar(true)
    } catch (e) {
      if (e instanceof SyntaxError) {
        alert(e.message)
      }
    }
  }

  const dropRef = useRef<HTMLDivElement>(null)
  const { dropProps, isDropTarget } = useDrop({
    ref: dropRef,
    async onDrop(e) {
      const item = e.items.find(x => x.kind === "file")
      if (!item) return

      await open(await item.getFile())
    },
  })

  const rightPanelKeyBase = activeEntry ? id(activeEntry) : "_null"

  const rightPanel = (
    <>
      <div css="flex">
        <Tabs
          css="m-2 inline-flex grow select-none"
          selectedKey={tab}
          onSelectionChange={t => setTab(t as ViewTab)}
        >
          <Tab key={ViewTab.Request} title={<TabTitle name={t`Request`} />} />
          <Tab key={ViewTab.Response} title={<TabTitle name={t`Response`} />} />
          <Tab key={ViewTab.Content} title={<TabTitle name={t`Content`} />} />
          <Tab key={ViewTab.Cookies} title={<TabTitle name={t`Cookies`} />} />
          <Tab key={ViewTab.Timeline} title={<TabTitle name={t`Timeline`} />} />
        </Tabs>

        <div ref={rightTab} />
      </div>
      <div css="grow overflow-scroll">
        <Boundary>
          {tab === ViewTab.Request && (
            <RequestTab entry={activeEntry} key={`${rightPanelKeyBase}_request`} />
          )}
          {tab === ViewTab.Response && <ResponseTab entry={activeEntry} />}
          {tab === ViewTab.Content && (
            <BodyTab
              entry={activeEntry}
              key={`${rightPanelKeyBase}_content`}
              rightSlot={rightTab}
            />
          )}
          {tab === ViewTab.Cookies && <CookiesTab entry={activeEntry} />}
          {tab === ViewTab.Timeline && <TimelineTab entry={activeEntry} />}
        </Boundary>
      </div>
    </>
  )

  const PanelIcon = showSidebar ? PanelLeftClose : PanelLeftOpen

  return (
    <div ref={dropRef} {...dropProps}>
      <title>{filename ?? "HAR Viewer"}</title>
      {isDropTarget && DROP_HAR_TIPS}
      <div className={RootGrid}>
        <div className={TopBar}>
          <div className="mx-1 my-0.5 flex items-center">
            <div css="flex grow items-center gap-1">
              <Button
                aria-label="Menu"
                css="-mr-2 ml-1 h-8 min-w-0 rounded-md bg-transparent px-2 hover:bg-gray-300 dark:hover:bg-gray-800"
                startContent={<PanelIcon css="opacity-60" size={19} />}
                title={showSidebar ? t`Hide sidebar` : t`Show sidebar`}
                onPress={() => setShowSidebar(!showSidebar)}
              />

              <Button
                aria-label="Open file"
                css="h-8 rounded-md bg-transparent py-1 hover:bg-gray-300 dark:hover:bg-gray-800"
                startContent={<FolderOpen css="opacity-60" size={16} />}
                onPress={() =>
                  openFileWithHandle({
                    description: t`HAR files`,
                    accept: {
                      "application/json": [".har", ".json"],
                    },
                  }).then(open)
                }
              >
                {t`Open file`}
              </Button>

              <hr
                className={css`
                  border: none;
                  border-right: 0.5px solid light-dark(#d6d6d6, #444);
                  height: 2em;
                  margin-right: 0.5em;
                `}
              />

              <div css="ml-1 w-36">
                <MethodFilter har={har} value={methodFilter} onChange={setMethodFilter} />
              </div>
              <div css="w-36">
                <StatusCodeFilter
                  har={har}
                  value={filterStatus}
                  onChange={setFilterStatus}
                />
              </div>
              <div>
                <HostTypeButton
                  excludeValue={excludeHost}
                  har={har}
                  includeValue={hostFilter}
                  onExcludeChange={setExcludeHost}
                  onIncludeChange={setHostFilter}
                />
              </div>
              <div>
                <MimeTypeButton
                  excludeValue={excludeMimeType}
                  har={har}
                  includeValue={mimeFilter}
                  onExcludeChange={setExcludeMimeType}
                  onIncludeChange={setMimeFilter}
                />
              </div>
            </div>

            <div css="flex items-center gap-0">
              {process.env.NODE_ENV === "development" && <DevPlayground />}
              <ViewStateDropdown
                compact={compact}
                getExport={exportState}
                onCompactChange={setCompact}
                onImport={json => {
                  const {
                    compact,
                    filterStatus,
                    methodFilter,
                    mimeFilter,
                    hostFilter,
                    excludeMimeType,
                    excludeHost,
                  } = json as ReturnType<typeof exportState>
                  setCompact(compact)
                  setFilterStatus(filterStatus)
                  setMethodFilter(methodFilter)
                  setMimeFilter(new Set(mimeFilter))
                  setHostFilter(new Set(hostFilter))
                  setExcludeHost(new Set(excludeHost))
                  setExcludeMimeType(new Set(excludeMimeType))
                }}
              />
              <ThemeSwitch />
              <LocaleSwitch />
              <AboutSwitch />
            </div>
          </div>
        </div>

        {!showSidebar ? (
          <div css="w-screen">{rightPanel}</div>
        ) : (
          <PanelGroup autoSaveId="har-view" className={Main} direction="horizontal">
            <Panel css="!overflow-y-scroll bg-default-50" defaultSize={40}>
              {har?.log.entries.length === 0 ? (
                <div css="mx-6 my-4 italic opacity-50">
                  <Trans>No entries available.</Trans>
                </div>
              ) : har ? (
                <Boundary>
                  <EntityList
                    compact={compact}
                    entries={entries}
                    selected={selected}
                    setSelected={setSelected}
                  />
                </Boundary>
              ) : (
                <div css="mx-6 my-4 italic opacity-50">
                  <Trans>Drag a HAR file here.</Trans>
                </div>
              )}
            </Panel>

            <PanelResizeHandle
              className={css`
                background-color: light-dark(#eee, #333);
                width: 0.5px;
              `}
            />
            <Panel css="flex flex-col !overflow-y-scroll">{rightPanel}</Panel>
          </PanelGroup>
        )}

        <div
          className={cx(
            css`
              border-top: 0.5px solid light-dark(#bfb8b1, #070707);
              grid-area: bottom-bar;
              padding: 7px 20px;
            `,
            "bg-default-100 truncate text-sm"
          )}
        >
          <Plural one="# request." other="# requests." value={entries.length} />{" "}
          {activeEntry != null && <span css="opacity-70">{activeEntry.request.url}</span>}
        </div>
      </div>
    </div>
  )
}

function TabTitle({ name }: { name: React.ReactNode }) {
  return <div css="flex items-center gap-1">{name}</div>
}

export default App
